import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Modal from "../../components/Modal";
import {
  deleteCategory,
  listCategories,
  listItemGroups,
  upsertCategory,
  type CategoryRecord,
} from "../../services/catalog";

const categorySchema = z.object({
  name: z.string().min(2, "Kategori adı zorunludur"),
  slug: z
    .string()
    .min(2, "Slug zorunludur")
    .regex(/^[a-z0-9-]+$/, "Küçük harf, rakam veya tire kullanın"),
  groupId: z.string().min(1, "Bir item group seçin"),
  parentCategoryId: z
    .string()
    .optional()
    .or(z.literal(""))
    .transform((value) => (value ? value : null)),
  displayOrder: z.coerce.number().min(0),
  isActive: z.boolean().default(true),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

const DEFAULT_VALUES: CategoryFormValues = {
  name: "",
  slug: "",
  groupId: "",
  parentCategoryId: "",
  displayOrder: 0,
  isActive: true,
};

const autoSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function CategoryManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryRecord | null>(null);

  const {
    data: categories = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["catalog", "categories"],
    queryFn: listCategories,
  });

  const {
    data: itemGroups = [],
    isLoading: groupsLoading,
    isFetching: groupsFetching,
  } = useQuery({
    queryKey: ["catalog", "item-groups"],
    queryFn: listItemGroups,
  });

  const groupsById = useMemo(() => {
    return Object.fromEntries(itemGroups.map((group) => [group.id, group]));
  }, [itemGroups]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: DEFAULT_VALUES,
  });

  const selectedGroupId = watch("groupId");

  const upsertMutation = useMutation({
    mutationFn: upsertCategory,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "categories"] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (category: CategoryRecord) => deleteCategory(category.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "categories"] });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Kategori silinirken bir hata oluştu. Lütfen tekrar deneyin.";
      window.alert(message);
    },
  });

  const parentCategoryOptions = useMemo(() => {
    if (!selectedGroupId) return [];
    return categories.filter(
      (category) =>
        category.groupId === selectedGroupId &&
        category.id !== editingCategory?.id,
    );
  }, [categories, selectedGroupId, editingCategory]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingCategory(null);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const openCreate = () => {
    setEditingCategory(null);
    reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (category: CategoryRecord) => {
    setEditingCategory(category);
    reset(
      {
        name: category.name,
        slug: category.slug,
        groupId: category.groupId,
        parentCategoryId: category.parentCategoryId ?? "",
        displayOrder: category.displayOrder,
        isActive: category.isActive,
      },
      { keepDefaultValues: false },
    );
    setModalOpen(true);
  };

  const onSubmit = (values: CategoryFormValues) => {
    const payload = {
      id: editingCategory?.id,
      name: values.name,
      slug: values.slug,
      displayOrder: Number(values.displayOrder),
      groupId: values.groupId,
      parentCategoryId: values.parentCategoryId,
      isActive: values.isActive,
    };
    return upsertMutation.mutateAsync(payload);
  };

  const handleNameBlur = () => {
    const name = watch("name");
    if (!editingCategory && name && !watch("slug")) {
      setValue("slug", autoSlug(name));
    }
  };

  const showGroupsEmptyState =
    !groupsLoading && itemGroups.length === 0 && !groupsFetching;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Menü kategorileri</p>
            <p className="card-subtitle">
              {isLoading || isFetching
                ? "Yükleniyor..."
                : `Toplam ${categories.length} kategori görüntüleniyor`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Kategori Ekle
          </button>
        </div>

        {showGroupsEmptyState ? (
          <div className="empty-state">
            <strong>Önce item group ekleyin</strong>
            <p>Kategoriler bir gruba bağlı olmalıdır. Yukarıdan grup oluşturun.</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz kategori yok</strong>
            <p>Yukarıdaki butonu kullanarak yeni kategori ekleyin.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sıra</th>
                  <th>Grup</th>
                  <th>Kategori</th>
                  <th>Slug</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td>{category.displayOrder}</td>
                    <td>{groupsById[category.groupId]?.name ?? "?"}</td>
                    <td>
                      <strong>{category.name}</strong>
                      {category.parentCategoryId ? (
                        <p className="helper-text">
                          Üst kategori:{" "}
                          {categories.find((c) => c.id === category.parentCategoryId)
                            ?.name ?? "—"}
                        </p>
                      ) : null}
                    </td>
                    <td>{category.slug}</td>
                    <td>
                      <span
                        className={`chip ${category.isActive ? "success" : "warning"}`}
                      >
                        {category.isActive ? "Yayında" : "Gizli"}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="button secondary"
                          onClick={() => openEdit(category)}
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (
                              window.confirm(
                                `${category.name} kategorisini silmek istediğinize emin misiniz?`,
                              )
                            ) {
                              deleteMutation.mutate(category);
                            }
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={
          editingCategory
            ? `${editingCategory.name} düzenleniyor`
            : "Yeni kategori ekle"
        }
      >
        <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
          <label className="form-field">
            <span>Ad</span>
            <input
              type="text"
              placeholder="Örn. Favori Pizzalar"
              {...register("name")}
              onBlur={handleNameBlur}
            />
            {errors.name ? <p className="error-text">{errors.name.message}</p> : null}
          </label>

          <label className="form-field">
            <span>Slug</span>
            <input type="text" placeholder="favori" {...register("slug")} />
            {errors.slug ? <p className="error-text">{errors.slug.message}</p> : null}
          </label>

          <label className="form-field">
            <span>Item group</span>
            <select {...register("groupId")}>
              <option value="">Grup seçin</option>
              {itemGroups.map((group) => (
                <option value={group.id} key={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            {errors.groupId ? (
              <p className="error-text">{errors.groupId.message}</p>
            ) : null}
          </label>

          <label className="form-field">
            <span>Üst kategori (opsiyonel)</span>
            <select {...register("parentCategoryId")}>
              <option value="">Seçim yok</option>
              {parentCategoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.parentCategoryId ? (
              <p className="error-text">{errors.parentCategoryId.message}</p>
            ) : null}
          </label>

          <label className="form-field">
            <span>Sıra</span>
            <input type="number" min={0} {...register("displayOrder")} />
            {errors.displayOrder ? (
              <p className="error-text">{errors.displayOrder.message}</p>
            ) : null}
          </label>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...register("isActive")} />
            <span>Aktif</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button className="button primary" type="submit" disabled={isSubmitting}>
              {editingCategory ? "Kategoriyi güncelle" : "Kategori ekle"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
